import React from 'react'
import { Typography } from 'react-components'
import { FormattedRule } from '../../../../models/property-details-page/booking-cancellation-policy-model'
import { useTranslation } from '../../../../hooks/locale'
import { useDateFormatter } from '../../../../hooks/date'
import { sub } from 'date-fns'
import isDate from 'lodash/isDate'
import { RuleItemsWrapper } from './styles'

interface RuleSubTextProps {
    rule: FormattedRule
    type: string
    isSlab: boolean
    index: number
    arrivalDate: Date | null
}

// Threshold to consider a rule as having a very high number of days before arrival
const HIGH_DAYS_TO_THRESHOLD = 400
const SUBTEXT_COLOR = 'tfwBlack40'

const isValidDate = (date: unknown): date is Date => isDate(date)

const RuleSubText: React.FC<RuleSubTextProps> = ({ rule, type, isSlab, index, arrivalDate }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()

    const subtractDays = (date: string | Date | null, days: number, dateFormat = 'dd MMM yyyy'): string => {
        if (!date || !isValidDate(new Date(date))) {
            return '--'
        }
        const originalDate = typeof date === 'string' ? new Date(date) : date
        return format(sub(originalDate, { days }), dateFormat)
    }

    const getSubTextKey = (rule: FormattedRule, type: string, isSlab: boolean): string => {
        const base = isSlab ? `slab.${type}` : type
        const hasArrivalDate = isValidDate(arrivalDate)

        const isZeroDays = isSlab && type !== 'fullRefund' && rule.daysFrom === 0 && rule.daysTo === 0
        const isHighDaysTo = isSlab && type === 'partialRefund' && rule.daysTo > HIGH_DAYS_TO_THRESHOLD

        if (isZeroDays) {
            return `bookingCancellationPolicy.${base}.subtextZeroDays`
        }
        if (isHighDaysTo) {
            return hasArrivalDate
                ? `bookingCancellationPolicy.${base}.subtextWithArrivalHighDaysTo`
                : `bookingCancellationPolicy.${base}.subtextHighDaysTo`
        }

        return hasArrivalDate && isSlab
            ? `bookingCancellationPolicy.${base}.subtextWithArrival`
            : `bookingCancellationPolicy.${base}.subText`
    }

    const getTranslationParams = (rule: FormattedRule) => ({
        daysFrom: rule.daysFrom,
        daysTo: rule.daysTo,
        percent: rule.percent,
        dateFrom: isValidDate(arrivalDate) ? subtractDays(arrivalDate, rule.daysFrom) : '--',
        dateTo: isValidDate(arrivalDate) ? subtractDays(arrivalDate, rule.daysTo) : '--',
    })

    const subTextKey = getSubTextKey(rule, type, isSlab)
    const translationParams = getTranslationParams(rule)

    return (
        <RuleItemsWrapper key={index}>
            <Typography variant="subtitle2" fontWeight="regular" color={SUBTEXT_COLOR}>
                {t(subTextKey, translationParams)}
            </Typography>
        </RuleItemsWrapper>
    )
}

export default RuleSubText
